"use client";

import { useState, useEffect, useMemo } from "react";

import { useTranslator } from "@eo-locale/react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { HistoryPlugin as LexicalHistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin as LexicalLinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListNode, ListItemNode } from "@lexical/list";
import { LinkNode, AutoLinkNode } from "@lexical/link";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { $getRoot, $insertNodes, EditorState } from "lexical";

import { useToken } from "@chakra-ui/react";

import { RichTextEditorProps } from "./types";
import { ToolbarPlugin } from "./plugins/ToolbarPlugin";
import { CharacterCountPlugin } from "./plugins/CharacterCountPlugin";
import { ClearFormattingPlugin } from "./plugins/ClearFormattingPlugin";
import { MaxLengthPlugin } from "./plugins/MaxLengthPlugin";
import PasteHandlerPlugin from "./plugins/PasteHandlerPlugin";
import { useEditorStyles } from "./theme";
import { isValidUrl } from "./utils/validateUrl";
import { getNormalizedTextContent } from "./utils/textContent";

// Inject minimal CSS for Lexical formatting classes
const injectEditorStyles = () => {
  if (
    typeof document === "undefined" ||
    document.getElementById("lexical-rte-styles")
  )
    return;

  const style = document.createElement("style");
  style.id = "lexical-rte-styles";
  style.textContent = `
    .editor-text-bold { font-weight: 700; }
    .editor-text-italic { font-style: italic; }
    .editor-text-underline { text-decoration: underline; }
    .editor-list-ul { margin: 0; padding-left: 20px; list-style-type: disc; }
    .editor-listitem { margin: 0; }
    .editor-link { color: #3182ce; text-decoration: underline; cursor: pointer; }
  `;
  document.head.appendChild(style);
};

export const RichTextEditor = ({
  id,
  value = "",
  onChange,
  placeholder,
  disabled = false,
  maxLength,
  allowLinks = false,
  errorMessage,
  invalid = false,
  required = false,
  editorStyles,
  toolbarStyles,
  characterCountStyles,
  containerStyles,
  ariaLabel,
  ariaDescribedBy,
  toolbarLabels
}: RichTextEditorProps): JSX.Element => {
  const [isFocused, setIsFocused] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [currentLength, setCurrentLength] = useState(0);
  const { translate } = useTranslator();

  const overLimit = maxLength != null && currentLength > maxLength;
  const hasError = (invalid && errorMessage) || linkError;
  const errorId = `${id}-error`;

  const computedAriaDescribedBy =
    [ariaDescribedBy, hasError ? errorId : null].filter(Boolean).join(" ") ||
    undefined;

  const themeStyles = useEditorStyles();
  const [red600, gray500, gray400] = useToken("colors", [
    "red.600",
    "gray.500",
    "gray.400"
  ]);
  const [space1, space2, space3] = useToken("spacing", ["1", "2", "3"]);
  const [fontSizeXs, fontSizeSm] = useToken("fontSizes", ["xs", "sm"]);
  const [size5] = useToken("sizes", ["5"]);

  const footerStyles = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: space1,
    padding: `${space1} ${space2}`,
    gap: space2,
    minHeight: size5
  };

  const footerErrorSectionStyles = {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: space1,
    minWidth: 0
  };

  const errorTextStyle = {
    color: red600,
    fontSize: fontSizeXs
  };

  const charCountStyle = {
    fontSize: fontSizeXs,
    color: overLimit ? red600 : gray500,
    flexShrink: 0,
    ...characterCountStyles
  };

  // Get translated toolbar labels or use provided overrides
  const translatedToolbarLabels = {
    bold: translate("common.toolbar.bold"),
    boldShortcut: translate("common.toolbar.boldShortcut"),
    italic: translate("common.toolbar.italic"),
    italicShortcut: translate("common.toolbar.italicShortcut"),
    underline: translate("common.toolbar.underline"),
    underlineShortcut: translate("common.toolbar.underlineShortcut"),
    unorderedList: translate("common.toolbar.unorderedList"),
    undo: translate("common.toolbar.undo"),
    undoShortcut: translate("common.toolbar.undoShortcut"),
    redo: translate("common.toolbar.redo"),
    redoShortcut: translate("common.toolbar.redoShortcut"),
    clearFormatting: translate("common.toolbar.clearFormatting"),
    removeLink: translate("common.toolbar.removeLink"),
    insertLink: translate("common.toolbar.insertLink"),
    list: translate("common.toolbar.list"),
    clear: translate("common.toolbar.clear"),
    ...toolbarLabels
  };

  // Inject styles on mount
  useEffect(() => {
    injectEditorStyles();
  }, []);

  const initialConfig = useMemo(
    () => ({
      namespace: `RichTextEditor-${id}`,
      theme: {
        text: {
          bold: "editor-text-bold",
          italic: "editor-text-italic",
          underline: "editor-text-underline"
        },
        list: { ul: "editor-list-ul", listitem: "editor-listitem" },
        link: "editor-link"
      },
      nodes: [ListNode, ListItemNode, LinkNode, AutoLinkNode],
      editorState: (editor: any) => {
        if (value && typeof window !== "undefined") {
          const parser = new window.DOMParser();
          const dom = parser.parseFromString(value, "text/html");
          const nodes = $generateNodesFromDOM(editor, dom);
          $getRoot().clear();
          $getRoot().select();
          $insertNodes(nodes);
        }
      },
      onError: (error: Error) => console.error("Lexical Error:", error)
    }),
    [id, value]
  );

  const handleChange = (editorState: EditorState, editor: any) => {
    editorState.read(() => {
      const htmlString = $generateHtmlFromNodes(editor, null);
      const plainText = getNormalizedTextContent();
      onChange(htmlString, plainText);
    });
  };

  const getEditorStyles = () => {
    let styles = { ...themeStyles.defaultEditorStyles, ...editorStyles };
    if (disabled) return { ...styles, ...themeStyles.disabledEditorStyles };
    if (invalid || overLimit)
      return { ...styles, ...themeStyles.invalidEditorStyles };
    if (isFocused) return { ...styles, ...themeStyles.focusedEditorStyles };
    return styles;
  };

  return (
    <div style={{ width: "100%", ...containerStyles }}>
      <LexicalComposer initialConfig={initialConfig}>
        {/* Toolbar */}
        {!disabled && (
          <ToolbarPlugin
            allowLinks={allowLinks}
            toolbarStyles={toolbarStyles}
            disabled={disabled}
            onLinkError={setLinkError}
            toolbarLabels={translatedToolbarLabels}
          />
        )}

        {/* Main Editor */}
        <div style={{ position: "relative" }}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                id={id}
                style={getEditorStyles()}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                tabIndex={0}
                aria-label={ariaLabel}
                aria-describedby={computedAriaDescribedBy}
                aria-required={required}
                aria-invalid={invalid || overLimit}
              />
            }
            placeholder={
              <div
                style={{
                  position: "absolute",
                  top: isFocused || invalid || overLimit ? "11px" : space3,
                  left: isFocused || invalid || overLimit ? "11px" : space3,
                  color: gray400,
                  pointerEvents: "none",
                  userSelect: "none",
                  fontSize: fontSizeSm
                }}
              >
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>

        {/* Plugins */}
        <OnChangePlugin onChange={handleChange} />
        <LexicalHistoryPlugin />
        <ListPlugin />
        <ClearFormattingPlugin disabled={disabled} />
        <PasteHandlerPlugin allowLinks={allowLinks} maxLength={maxLength} />
        {allowLinks && <LexicalLinkPlugin validateUrl={isValidUrl} />}
        {maxLength && <MaxLengthPlugin maxLength={maxLength} />}

        {/* Character Count */}
        {maxLength && <CharacterCountPlugin onCountChange={setCurrentLength} />}
      </LexicalComposer>

      {/* Footer: error message (left) | character count (right) */}
      {(hasError || maxLength) && (
        <div style={footerStyles}>
          <div style={footerErrorSectionStyles}>
            {hasError ? (
              <span id={errorId} role="alert" style={errorTextStyle}>
                {linkError || errorMessage}
              </span>
            ) : null}
          </div>
          {maxLength ? (
            <div style={charCountStyle}>
              {`${currentLength.toLocaleString()} / ${maxLength.toLocaleString()}`}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export * from "./types";
